import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { showBrowseToast } from './communitiesBrowseStore';

const STORAGE_KEY = 'al-community-favorites';
const CHANGE_EVENT = 'al-community-favorites-change';

function readIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter(Boolean);
  } catch {
    return [];
  }
}

function writeIds(ids: string[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

let cachedSnapshot = '';
let cachedIds: string[] = [];

function getSnapshot(): string[] {
  const next = readIds();
  const key = next.join('\0');
  if (key === cachedSnapshot) return cachedIds;
  cachedSnapshot = key;
  cachedIds = next;
  return cachedIds;
}

function getServerSnapshot(): string[] {
  return [];
}

function subscribe(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function useCommunityFavoriteIds(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function useCommunityFavorite(propertyId: string | null | undefined) {
  const ids = useCommunityFavoriteIds();
  const id = propertyId ? String(propertyId) : '';
  const favorited = Boolean(id && ids.includes(id));

  const toggle = useCallback(() => {
    if (!id) return;
    const current = readIds();
    const removing = current.includes(id);
    writeIds(removing ? current.filter((item) => item !== id) : [...current, id]);
    showBrowseToast(removing ? 'Removed from Favorites' : 'Saved to Favorites');
  }, [id]);

  return { favorited, toggle };
}

/** Merge ids (e.g. from a shared list) into this browser's favorites; returns how many were new. */
export function addFavoriteIds(ids: string[]): number {
  const current = readIds();
  const additions = ids.map(String).filter((id) => id && !current.includes(id));
  if (additions.length > 0) writeIds([...current, ...additions]);
  return additions.length;
}

/** Hydration-safe favorite count for UI badges. */
export function useFavoriteCount(): number {
  const ids = useCommunityFavoriteIds();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  return ready ? ids.length : 0;
}
